// client/src/components/AddDealerModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { getAuthHeaders } from '../utils/authHelpers';

const API_ROOT_URL = process.env.REACT_APP_API_ROOT_URL;
const DEALERS_API_URL = `${API_ROOT_URL}/dealers`;

function AddDealerModal({ show, handleClose, refreshDealers, setSubmissionMessage }) {
  const [formData, setFormData] = useState({
    dealerName: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    },
    shippingPreferences: '',
    isActive: true, // Default to active for new dealers
  });

  // Inline validation state
  const [errors, setErrors] = useState({
    dealerName: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: { street: '', city: '', state: '', zip: '', country: '' },
    shippingPreferences: ''
  });
  const [touched, setTouched] = useState({
    dealerName: false,
    contactPerson: false,
    contactEmail: false,
    contactPhone: false,
    address: { street: false, city: false, state: false, zip: false, country: false },
    shippingPreferences: false
  });

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(''); // summary message (optional)

  // ===== validators =====
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

  const validateField = (name, value) => {
    switch (name) {
      case 'dealerName':
        return value.trim() ? '' : 'Dealer Name is required.';
      case 'contactPerson':
        return value.trim() ? '' : 'Contact Person is required.';
      case 'contactEmail':
        if (!value.trim()) return 'Contact Email is required.';
        if (!emailRegex.test(value.trim())) return 'Please enter a valid email address.';
        return '';
      case 'contactPhone': {
        // Optional; if provided, require 7–15 digits after stripping non-digits
        const digits = value.replace(/\D/g, '');
        if (!value.trim()) return '';
        if (digits.length < 7 || digits.length > 15) return 'Please enter a valid phone number (7–15 digits).';
        return '';
      }
      case 'address.street':
      case 'address.city':
      case 'address.state':
      case 'address.country':
        // Optional fields – no error unless you want to enforce
        return '';
      case 'address.zip': {
        // Optional; if provided, simple check: 3–10 chars (digits/letters)
        const zip = value.trim();
        if (!zip) return '';
        if (!/^[A-Za-z0-9\- ]{3,10}$/.test(zip)) return 'Please enter a valid postal/zip code.';
        return '';
      }
      case 'shippingPreferences':
        return ''; // optional
      default:
        return '';
    }
  };

  const validateAll = (data) => {
    const nextErrors = {
      dealerName: validateField('dealerName', data.dealerName),
      contactPerson: validateField('contactPerson', data.contactPerson),
      contactEmail: validateField('contactEmail', data.contactEmail),
      contactPhone: validateField('contactPhone', data.contactPhone),
      address: {
        street: validateField('address.street', data.address.street),
        city: validateField('address.city', data.address.city),
        state: validateField('address.state', data.address.state),
        zip: validateField('address.zip', data.address.zip),
        country: validateField('address.country', data.address.country),
      },
      shippingPreferences: validateField('shippingPreferences', data.shippingPreferences)
    };

    // Get first error message (for summary alert)
    const flatErrors = [
      nextErrors.dealerName,
      nextErrors.contactPerson,
      nextErrors.contactEmail,
      nextErrors.contactPhone,
      nextErrors.address.street,
      nextErrors.address.city,
      nextErrors.address.state,
      nextErrors.address.zip,
      nextErrors.address.country,
      nextErrors.shippingPreferences
    ].filter(Boolean);
    const firstError = flatErrors[0] || '';

    return { nextErrors, firstError };
  };

  const markAllTouched = () => {
    setTouched({
      dealerName: true,
      contactPerson: true,
      contactEmail: true,
      contactPhone: true,
      address: { street: true, city: true, state: true, zip: true, country: true },
      shippingPreferences: true
    });
  };

  // Reset form data and messages when the modal is shown
  useEffect(() => {
    if (show) {
      setFormData({
        dealerName: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
          country: ''
        },
        shippingPreferences: '',
        isActive: true,
      });
      setErrors({
        dealerName: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        address: { street: '', city: '', state: '', zip: '', country: '' },
        shippingPreferences: ''
      });
      setTouched({
        dealerName: false,
        contactPerson: false,
        contactEmail: false,
        contactPhone: false,
        address: { street: false, city: false, state: false, zip: false, country: false },
        shippingPreferences: false
      });
      setFormError('');
      setSubmissionMessage('', ''); // Clear any previous messages from parent
    }
  }, [show, setSubmissionMessage]);

  const setNestedState = (setter, pathArr, value) => {
    setter(prev => {
      const next = structuredClone(prev);
      let node = next;
      for (let i = 0; i < pathArr.length - 1; i++) {
        node[pathArr[i]] = { ...node[pathArr[i]] };
        node = node[pathArr[i]];
      }
      node[pathArr[pathArr.length - 1]] = value;
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));

      // Validate this address field on change
      const errMsg = validateField(name, value);
      setNestedState(setErrors, ['address', addressField], errMsg);
      setNestedState(setTouched, ['address', addressField], true);
    } else {
      const newValue = type === 'checkbox' ? checked : value;
      setFormData(prev => ({ ...prev, [name]: newValue }));

      // Validate this field on change
      const errMsg = validateField(name, newValue);
      setErrors(prev => ({ ...prev, [name]: errMsg }));
      setTouched(prev => ({ ...prev, [name]: true }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setNestedState(setTouched, ['address', addressField], true);
      const value = formData.address[addressField];
      const errMsg = validateField(name, value);
      setNestedState(setErrors, ['address', addressField], errMsg);
    } else {
      setTouched(prev => ({ ...prev, [name]: true }));
      const value = formData[name];
      const errMsg = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: errMsg }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormError('');
    setSubmissionMessage('', ''); // Clear previous server-side messages

    // Full validation before submit
    const { nextErrors, firstError } = validateAll(formData);
    setErrors(nextErrors);
    markAllTouched();

    const hasErrors =
      firstError ||
      Object.values(nextErrors).some(v => {
        if (typeof v === 'string') return !!v;
        return Object.values(v).some(Boolean); // for nested address
      });

    if (hasErrors) {
      setFormError(firstError || 'Please fix the highlighted fields.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(DEALERS_API_URL, {
        method: 'POST',
        headers: getAuthHeaders(), // Use Authorization + Content-Type
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmissionMessage(result.message || 'Dealer added successfully!', 'success');
        refreshDealers(); // Refresh the list
        onHide(); // Close modal
      } else {
        setSubmissionMessage(result.message || 'Failed to add dealer.', 'danger');
      }
    } catch (error) {
      console.error('Error adding dealer:', error);
      setSubmissionMessage('Network error or server is unreachable. Please try again.', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Reset form and close modal
  const onHide = () => {
    setFormData({
      dealerName: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
        country: ''
      },
      shippingPreferences: '',
      isActive: true,
    });
    setErrors({
      dealerName: '',
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      address: { street: '', city: '', state: '', zip: '', country: '' },
      shippingPreferences: ''
    });
    setTouched({
      dealerName: false,
      contactPerson: false,
      contactEmail: false,
      contactPhone: false,
      address: { street: false, city: false, state: false, zip: false, country: false },
      shippingPreferences: false
    });
    setFormError('');
    handleClose();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add New Dealer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {formError && <Alert variant="danger">{formError}</Alert>}
        <Form onSubmit={handleSubmit} noValidate>
          <h5 className="mb-3">Dealer Information</h5>
          <Form.Group className="mb-3" controlId="dealerName">
            <Form.Label>Dealer Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="dealerName"
              value={formData.dealerName}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter dealer name"
              required
              disabled={loading}
              isInvalid={touched.dealerName && !!errors.dealerName}
            />
            <Form.Control.Feedback type="invalid">{errors.dealerName}</Form.Control.Feedback>
          </Form.Group>

          <Row className="mb-3">
            <Form.Group as={Col} controlId="contactPerson">
              <Form.Label>Contact Person <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter contact person's name"
                required
                disabled={loading}
                isInvalid={touched.contactPerson && !!errors.contactPerson}
              />
              <Form.Control.Feedback type="invalid">{errors.contactPerson}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="contactEmail">
              <Form.Label>Contact Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter contact email"
                required
                disabled={loading}
                pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
                title="Enter a valid email address (e.g., name@example.com)"
                isInvalid={touched.contactEmail && !!errors.contactEmail}
              />
              <Form.Control.Feedback type="invalid">{errors.contactEmail}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Form.Group className="mb-3" controlId="contactPhone">
            <Form.Label>Contact Phone</Form.Label>
            <Form.Control
              type="text"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter contact phone number"
              disabled={loading}
              inputMode="tel"
              isInvalid={touched.contactPhone && !!errors.contactPhone}
            />
            <Form.Text muted>Optional. Digits only; 7–15 digits.</Form.Text>
            <Form.Control.Feedback type="invalid">{errors.contactPhone}</Form.Control.Feedback>
          </Form.Group>

          <h5 className="mb-3 mt-4">Address Information</h5>
          <Form.Group className="mb-3" controlId="address.street">
            <Form.Label>Street</Form.Label>
            <Form.Control
              type="text"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Enter street address"
              disabled={loading}
              isInvalid={touched.address.street && !!errors.address.street}
            />
            <Form.Control.Feedback type="invalid">{errors.address.street}</Form.Control.Feedback>
          </Form.Group>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="address.city">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter city"
                disabled={loading}
                isInvalid={touched.address.city && !!errors.address.city}
              />
              <Form.Control.Feedback type="invalid">{errors.address.city}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="address.state">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter state"
                disabled={loading}
                isInvalid={touched.address.state && !!errors.address.state}
              />
              <Form.Control.Feedback type="invalid">{errors.address.state}</Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="address.zip">
              <Form.Label>Zip Code</Form.Label>
              <Form.Control
                type="text"
                name="address.zip"
                value={formData.address.zip}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter zip code"
                disabled={loading}
                isInvalid={touched.address.zip && !!errors.address.zip}
              />
              <Form.Control.Feedback type="invalid">{errors.address.zip}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group as={Col} controlId="address.country">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter country"
                disabled={loading}
                isInvalid={touched.address.country && !!errors.address.country}
              />
              <Form.Control.Feedback type="invalid">{errors.address.country}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <h5 className="mb-3 mt-4">Other Preferences</h5>
          <Form.Group className="mb-3" controlId="shippingPreferences">
            <Form.Label>Shipping Preferences</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="shippingPreferences"
              value={formData.shippingPreferences}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Any specific shipping instructions or preferences"
              disabled={loading}
              isInvalid={touched.shippingPreferences && !!errors.shippingPreferences}
            />
            <Form.Control.Feedback type="invalid">{errors.shippingPreferences}</Form.Control.Feedback>
          </Form.Group>

          <Button variant="primary" type="submit" disabled={loading} className="w-100 mt-4">
            {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Add Dealer'}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddDealerModal;
